import { useState, useEffect, useCallback } from 'react';
import { Spin, notification, Input, Modal, Select } from 'antd';
import {
  BankFilled, BankOutlined, ClockCircleOutlined, EnvironmentOutlined, PhoneOutlined, SearchOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang, toTitle } from '../shared/utils';
import { useDebounce } from '../shared/hooks';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const JOB_TYPES     = ['Full Time','Part Time','Contract','Freelance','Other'];
const JOB_CATS      = ['government','private','business','agriculture','teaching','medical','it','nri','other'];
const JOB_CAT_LABEL = { government:'सरकारी', private:'Private', business:'व्यवसाय', agriculture:'कृषि', teaching:'शिक्षा', medical:'Medical', it:'IT/Tech', nri:'NRI Jobs', other:'अन्य' };
const JOB_TYPE_COLOR= { 'Full Time':M, 'Part Time':'#7c3aed', 'Contract':'#0891b2', 'Freelance':'#b45309', 'Other':'#6b7280' };

const iStyle = { width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', background:'#fff' };
const lStyle = { fontSize:12, fontWeight:700, color:'#374151', marginBottom:4, display:'block' };

export function JobsTab({ profile }) {
  const { t } = useLang();
  const [view,       setView]       = useState('browse'); // browse|mine|seekers|apply|seeker_form
  const [jobs,       setJobs]       = useState([]);
  const [myJobs,     setMyJobs]     = useState([]);
  const [myApps,     setMyApps]     = useState([]);
  const [seekers,    setSeekers]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [postOpen,   setPostOpen]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewJob,    setViewJob]    = useState(null);
  const [applying,   setApplying]   = useState(null); // job being applied to
  const [applyMsg,   setApplyMsg]   = useState('');
  const [applyLoading,setApplyLoading] = useState(false);
  const [applicants, setApplicants] = useState(null);
  const [mySeeker,   setMySeeker]   = useState(null);
  const [seekerForm, setSeekerForm] = useState(null);
  const [form, setForm] = useState({
    title:'', company_name:'', category:'private', job_type:'Full Time',
    location_city:'', location_state:'', location_country:'India', is_remote:false,
    salary_range:'', description:'', requirements:'', contact_phone:'', contact_name:'', last_date:'',
  });
  const dSearch = useDebounce(search, 400);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: 30 });
      if (dSearch)    p.set('search', dSearch);
      if (catFilter)  p.set('category', catFilter);
      if (typeFilter) p.set('job_type', typeFilter);
      const [jRes, mRes, aRes, sRes, skRes] = await Promise.all([
        axios.get(`${baseUrl}/member/jobs/public?${p}`, { headers: authHeader() }),
        axios.get(`${baseUrl}/member/jobs/mine`, { headers: authHeader() }),
        axios.get(`${baseUrl}/member/jobs/my-applications`, { headers: authHeader() }),
        axios.get(`${baseUrl}/member/job-seekers`, { headers: authHeader() }),
        axios.get(`${baseUrl}/member/job-seeker/me`, { headers: authHeader() }),
      ]);
      if (jRes.data.status)  setJobs(jRes.data.result || []);
      if (mRes.data.status)  setMyJobs(mRes.data.result || []);
      if (aRes.data.status)  setMyApps(aRes.data.result || []);
      if (sRes.data.status)  setSeekers(sRes.data.result || []);
      if (skRes.data.status) {
        setMySeeker(skRes.data.result);
        if (skRes.data.result) setSeekerForm({ ...skRes.data.result });
      }
    } catch {}
    finally { setLoading(false); }
  }, [dSearch, catFilter, typeFilter]); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const postJob = async () => {
    if (!form.title?.trim())        { notification.warning({ message: t('Job title जरूरी है','Job title is required'), placement: 'top' }); return; }
    if (!form.location_city?.trim()){ notification.warning({ message: t('City जरूरी है','City is required'), placement: 'top' }); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${baseUrl}/member/jobs`, form, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: t('Job submitted for admin review ✅','Job submitted for admin review ✅'), placement: 'top' });
        setPostOpen(false);
        setForm({ title:'', company_name:'', category:'private', job_type:'Full Time', location_city:'', location_state:'', location_country:'India', is_remote:false, salary_range:'', description:'', requirements:'', contact_phone:'', contact_name:'', last_date:'' });
        fetchAll();
      } else notification.error({ message: res.data.message, placement: 'top' });
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'top' }); }
    finally { setSubmitting(false); }
  };

  const deleteJob = async (id) => {
    try {
      await axios.delete(`${baseUrl}/member/jobs/${id}`, { headers: authHeader() });
      notification.success({ message: t('हटाया गया','Deleted'), placement: 'top' });
      fetchAll();
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'top' }); }
  };

  const submitApply = async () => {
    if (!applying) return;
    setApplyLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/member/jobs/${applying.id}/apply`, { message: applyMsg }, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: t('आवेदन भेजा गया! ✅','Application sent! ✅'), placement: 'top' });
        setApplying(null); setApplyMsg(''); fetchAll();
      } else notification.error({ message: res.data.message, placement: 'top' });
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'top' }); }
    finally { setApplyLoading(false); }
  };

  const fetchApplicants = async (job) => {
    setApplicants({ loading: true, job, list: [] });
    try {
      const res = await axios.get(`${baseUrl}/member/jobs/${job.id}/applicants`, { headers: authHeader() });
      if (res.data.status) setApplicants({ loading: false, job, list: res.data.result || [] });
    } catch { setApplicants(a => ({ ...a, loading: false })); }
  };

  const saveSeekerProfile = async () => {
    if (!seekerForm?.headline?.trim()) { notification.warning({ message: t('Headline जरूरी है','Headline is required'), placement: 'top' }); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${baseUrl}/member/job-seeker`, seekerForm, { headers: authHeader() });
      if (res.data.status) { notification.success({ message: t('Profile saved! ✅','Profile saved! ✅'), placement: 'top' }); setView('seekers'); fetchAll(); }
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'top' }); }
    finally { setSubmitting(false); }
  };

  const TABS = [
    { k:'browse',  l:t('नौकरियाँ','Jobs'),        cnt: jobs.length > 0 ? jobs.length : null },
    { k:'seekers', l:t('Job Seekers','Job Seekers'), cnt: seekers.length > 0 ? seekers.length : null },
    { k:'mine',    l:t('मेरी Posts','My Posts'),    cnt: myJobs.length > 0 ? myJobs.length : null },
    { k:'applied', l:t('Applied','Applied'),         cnt: myApps.length > 0 ? myApps.length : null },
  ];

  const STATUS_CFG = {
    Pending:  { bg:'#fffbeb', color:'#92400e', label: t('⏳ Pending','⏳ Pending') },
    Approved: { bg:'#f0fdf4', color:'#15803d', label: t('✅ Approved','✅ Approved') },
    Rejected: { bg:'#fef2f2', color:'#991b1b', label: t('❌ Rejected','❌ Rejected') },
    Expired:  { bg:'#f9fafb', color:'#6b7280', label: t('⌛ Expired','⌛ Expired') },
    Filled:   { bg:'#eff6ff', color:'#1d4ed8', label: t('✔️ Filled','✔️ Filled') },
  };

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ background:`linear-gradient(135deg,${M},#b71c1c)`, borderRadius:14, padding:'14px 16px', marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:15, fontWeight:900, color:'#fff', display:'flex', alignItems:'center', gap:8 }}>
            <BankFilled style={{ fontSize:18 }} /> {t('रोजगार सेतु','Rozgar Setu')}
          </div>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', marginTop:2 }}>
            {t('समाज के सदस्यों द्वारा नौकरियाँ व Job Seekers','Community jobs & job seekers')}
          </div>
        </div>
        <button onClick={() => setPostOpen(true)} style={{ background:'#fff', color:M, border:'none', borderRadius:10, padding:'8px 14px', fontWeight:800, fontSize:12, cursor:'pointer', flexShrink:0 }}>
          + {t('Post Job','Post Job')}
        </button>
      </div>

      {/* ── Nav tabs ── */}
      <div style={{ display:'flex', borderBottom:`2px solid #f3f4f6`, marginBottom:14, overflowX:'auto' }}>
        {TABS.map(tab => (
          <button key={tab.k} onClick={() => setView(tab.k)} style={{
            background:'none', border:'none', padding:'9px 14px', cursor:'pointer', whiteSpace:'nowrap',
            fontWeight: view===tab.k ? 800 : 500, fontSize:13,
            color: view===tab.k ? M : '#6b7280',
            borderBottom: view===tab.k ? `2.5px solid ${M}` : '2.5px solid transparent',
            display:'flex', alignItems:'center', gap:5,
          }}>
            {tab.l}
            {tab.cnt && <span style={{ background:`${M}20`, color:M, borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:800 }}>{tab.cnt}</span>}
          </button>
        ))}
      </div>

      {/* ── Browse Jobs ── */}
      {view === 'browse' && (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
            <Input prefix={<SearchOutlined style={{ color:'#9ca3af' }}/>}
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('Job title, city खोजें…','Search job title, city…')}
              allowClear style={{ flex:1, minWidth:160, borderRadius:8 }} />
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...iStyle, width:'auto', flex:'0 0 auto' }}>
              <option value="">{t('सभी श्रेणी','All Categories')}</option>
              {JOB_CATS.map(c => <option key={c} value={c}>{JOB_CAT_LABEL[c]}</option>)}
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ ...iStyle, width:'auto', flex:'0 0 auto' }}>
              <option value="">{t('सभी प्रकार','All Types')}</option>
              {JOB_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          </div>

          {loading && <div style={{ textAlign:'center', padding:40 }}><Spin /></div>}
          {!loading && jobs.length === 0 && (
            <div style={{ textAlign:'center', padding:48, color:'#9ca3af' }}>
              <BankFilled style={{ fontSize:36, display:'block', margin:'0 auto 10px', color:'#e5e7eb' }} />
              <div>{t('अभी कोई नौकरी उपलब्ध नहीं है।','No jobs available right now.')}</div>
              <div style={{ fontSize:11, marginTop:4 }}>{t('पहली job post करें!','Be the first to post a job!')}</div>
            </div>
          )}
          {!loading && jobs.map(j => {
            const tc = JOB_TYPE_COLOR[j.job_type] || M;
            const alreadyApplied = myApps.some(a => a.id === j.id);
            const isMyJob = j.posted_by === profile?.id;
            return (
              <div key={j.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', padding:'14px', marginBottom:10, boxShadow:'0 1px 5px rgba(0,0,0,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:800, color:'#111827', marginBottom:3 }}>{j.title}</div>
                    {j.company_name && <div style={{ fontSize:12, color:'#374151', marginBottom:6, display:'flex', alignItems:'center', gap:5 }}><BankOutlined style={{ color:'#9ca3af', fontSize:12 }}/> {j.company_name}</div>}
                    <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                      <span style={{ background:`${tc}15`, color:tc, borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:700 }}>{j.job_type}</span>
                      {j.category && j.category !== 'private' && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 9px', fontSize:11 }}>{JOB_CAT_LABEL[j.category]||j.category}</span>}
                      {j.location_city && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 9px', fontSize:11 }}><EnvironmentOutlined style={{ fontSize:10 }}/> {j.location_city}{j.location_state?`, ${j.location_state}`:''}</span>}
                      {j.is_remote === 1 && <span style={{ background:'#eff6ff', color:'#1d4ed8', borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:700 }}>Remote</span>}
                      {j.salary_range && <span style={{ background:'#f0fdf4', color:'#15803d', borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:600 }}>₹ {j.salary_range}</span>}
                    </div>
                  </div>
                  <div style={{ fontSize:10, color:'#9ca3af', flexShrink:0, textAlign:'right' }}>
                    <div>{new Date(j.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</div>
                    {j.application_count > 0 && <div style={{ marginTop:2, color:M, fontWeight:700 }}>{j.application_count} {t('आवेदन','applied')}</div>}
                  </div>
                </div>

                {j.poster_name && (
                  <div style={{ fontSize:10, color:'#9ca3af', marginTop:6, display:'flex', alignItems:'center', gap:4 }}>
                    <UserOutlined style={{ fontSize:10 }}/> {t('Posted by','Posted by')}: <span style={{ color:M, fontWeight:600 }}>{j.poster_name}</span>
                    {j.poster_village && ` · ${j.poster_village}`}
                  </div>
                )}
                {j.last_date && <div style={{ fontSize:10, color:'#dc2626', marginTop:5, fontWeight:600 }}><ClockCircleOutlined style={{ fontSize:10 }}/> {t('अंतिम तिथि','Last date')}: {new Date(j.last_date).toLocaleDateString('en-IN')}</div>}

                <div style={{ display:'flex', gap:8, marginTop:10 }}>
                  <button onClick={() => setViewJob(j)} style={{ flex:1, background:'#f9fafb', border:`1px solid ${M}30`, borderRadius:9, padding:'8px 0', fontSize:12, fontWeight:700, color:M, cursor:'pointer' }}>
                    {t('विवरण देखें','View Details')}
                  </button>
                  {!isMyJob && (
                    <button
                      onClick={() => { if (!alreadyApplied) { setApplying(j); setApplyMsg(''); } }}
                      disabled={alreadyApplied}
                      style={{ flex:1.5, background: alreadyApplied ? '#f0fdf4' : M, border:'none', borderRadius:9, padding:'8px 0', fontSize:12, fontWeight:800, color: alreadyApplied ? '#15803d' : '#fff', cursor: alreadyApplied ? 'default' : 'pointer' }}>
                      {alreadyApplied ? t('✅ Applied','✅ Applied') : t('Apply करें →','Apply Now →')}
                    </button>
                  )}
                  {isMyJob && (
                    <button onClick={() => fetchApplicants(j)} style={{ flex:1.5, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:9, padding:'8px 0', fontSize:12, fontWeight:800, color:'#1d4ed8', cursor:'pointer' }}>
                      {t('आवेदक देखें','View Applicants')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── Job Seekers ── */}
      {view === 'seekers' && (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:13, color:'#6b7280' }}>{t('नौकरी ढूंढ रहे सदस्य','Members looking for jobs')} ({seekers.length})</div>
            <button onClick={() => { setSeekerForm(mySeeker || { headline:'', qualification:'', experience_years:0, skills:'', current_city:'', current_state:'', current_country:'India', open_to_relocation:true, preferred_cities:'', salary_expected:'', bio:'', category:'', is_active:true }); setView('seeker_form'); }}
              style={{ background:M, color:'#fff', border:'none', borderRadius:8, padding:'7px 13px', fontSize:12, fontWeight:800, cursor:'pointer' }}>
              {mySeeker ? t('Profile Edit करें','Edit My Profile') : t('+ Profile बनाएं','+ Create Profile')}
            </button>
          </div>
          {loading && <div style={{ textAlign:'center', padding:32 }}><Spin /></div>}
          {!loading && seekers.length === 0 && (
            <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>
              <UserOutlined style={{ fontSize:36, display:'block', margin:'0 auto 10px', color:'#e5e7eb' }}/>
              <div>{t('अभी कोई Job Seeker नहीं है।','No job seekers yet.')}</div>
            </div>
          )}
          {!loading && seekers.map(s => (
            <div key={s.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', padding:'13px 14px', marginBottom:10, display:'flex', gap:12, alignItems:'flex-start', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ position:'relative', flexShrink:0 }}>
                {s.photo ? <img src={s.photo} alt={s.name} style={{ width:46, height:46, borderRadius:'50%', objectFit:'cover', objectPosition:'top', border:`2px solid ${M}` }}/> :
                  <div style={{ width:46, height:46, borderRadius:'50%', background:M, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:800, color:'#FFD700' }}>{(s.name||'?')[0]}</div>}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{s.name}</div>
                <div style={{ fontSize:12, fontWeight:600, color:M, marginTop:2 }}>{s.headline}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:6 }}>
                  {s.qualification && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}>{s.qualification}</span>}
                  {s.experience_years > 0 && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}>{s.experience_years} yr exp</span>}
                  {s.current_city && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'2px 8px', fontSize:10 }}><EnvironmentOutlined style={{ fontSize:9 }}/> {toTitle(s.current_city)}</span>}
                  {s.salary_expected && <span style={{ background:'#f0fdf4', color:'#15803d', borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:600 }}>₹ {s.salary_expected}</span>}
                  {s.open_to_relocation === 1 && <span style={{ background:'#eff6ff', color:'#1d4ed8', borderRadius:20, padding:'2px 8px', fontSize:10 }}>Open to Relocate</span>}
                </div>
                {s.skills && <div style={{ fontSize:10, color:'#6b7280', marginTop:5 }}><strong>Skills:</strong> {s.skills}</div>}
                {s.native_village && <div style={{ fontSize:10, color:'#9ca3af', marginTop:3 }}>{t('मूल','From')}: {s.native_village}</div>}
              </div>
              {s.membership_no && <div style={{ fontSize:10, color:'#9ca3af', flexShrink:0 }}>#{s.membership_no}</div>}
            </div>
          ))}
        </>
      )}

      {/* ── Seeker Profile Form ── */}
      {view === 'seeker_form' && seekerForm && (
        <div>
          <button onClick={() => setView('seekers')} style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:700, color:'#374151', marginBottom:14 }}>
            ← {t('वापस','Back')}
          </button>
          <div style={{ background:'#fff', borderRadius:14, border:'1px solid #e5e7eb', padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,0.07)' }}>
            <div style={{ fontSize:14, fontWeight:800, color:M, marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
              <UserOutlined /> {mySeeker ? t('Job Seeker Profile Update','Update Job Seeker Profile') : t('Job Seeker Profile बनाएं','Create Job Seeker Profile')}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lStyle}>{t('Headline *','Headline *')}</label>
                <input style={iStyle} value={seekerForm.headline||''} onChange={e=>setSeekerForm(f=>({...f,headline:e.target.value}))} placeholder={t('e.g. CA looking for senior role in Jaipur','e.g. CA looking for senior role in Jaipur')} maxLength={300}/>
              </div>
              <div><label style={lStyle}>{t('योग्यता','Qualification')}</label><input style={iStyle} value={seekerForm.qualification||''} onChange={e=>setSeekerForm(f=>({...f,qualification:e.target.value}))} maxLength={200}/></div>
              <div><label style={lStyle}>{t('अनुभव (वर्ष)','Experience (years)')}</label><input type="number" style={iStyle} value={seekerForm.experience_years||0} onChange={e=>setSeekerForm(f=>({...f,experience_years:parseInt(e.target.value)||0}))} min={0} max={50}/></div>
              <div><label style={lStyle}>{t('वर्तमान शहर','Current City')}</label><input style={iStyle} value={seekerForm.current_city||''} onChange={e=>setSeekerForm(f=>({...f,current_city:e.target.value}))} maxLength={100}/></div>
              <div><label style={lStyle}>{t('राज्य','State')}</label><input style={iStyle} value={seekerForm.current_state||''} onChange={e=>setSeekerForm(f=>({...f,current_state:e.target.value}))} maxLength={100}/></div>
              <div><label style={lStyle}>{t('अपेक्षित वेतन','Expected Salary')}</label><input style={iStyle} value={seekerForm.salary_expected||''} onChange={e=>setSeekerForm(f=>({...f,salary_expected:e.target.value}))} placeholder="e.g. ₹30,000/month" maxLength={100}/></div>
              <div>
                <label style={lStyle}>{t('Category','Field of Work')}</label>
                <select style={iStyle} value={seekerForm.category||''} onChange={e=>setSeekerForm(f=>({...f,category:e.target.value}))}>
                  <option value="">{t('चुनें','Select')}</option>
                  {JOB_CATS.map(c=><option key={c} value={c}>{JOB_CAT_LABEL[c]}</option>)}
                </select>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lStyle}>{t('Skills (comma separated)','Skills (comma separated)')}</label>
                <input style={iStyle} value={seekerForm.skills||''} onChange={e=>setSeekerForm(f=>({...f,skills:e.target.value}))} placeholder="e.g. Accounting, Tally, MS Excel" maxLength={500}/>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lStyle}>{t('Preferred Cities','Preferred Cities')}</label>
                <input style={iStyle} value={seekerForm.preferred_cities||''} onChange={e=>setSeekerForm(f=>({...f,preferred_cities:e.target.value}))} placeholder="Jaipur, Mumbai, Bengaluru" maxLength={300}/>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lStyle}>{t('परिचय (Bio)','About You (Bio)')}</label>
                <textarea style={{ ...iStyle, height:70, resize:'vertical' }} value={seekerForm.bio||''} onChange={e=>setSeekerForm(f=>({...f,bio:e.target.value}))} maxLength={500}/>
              </div>
              <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" id="relocate" checked={!!seekerForm.open_to_relocation} onChange={e=>setSeekerForm(f=>({...f,open_to_relocation:e.target.checked}))}/>
                <label htmlFor="relocate" style={{ fontSize:13, color:'#374151', cursor:'pointer' }}>{t('स्थानांतरण के लिए तैयार हूँ','Open to relocation')}</label>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:16 }}>
              <button onClick={() => setView('seekers')} style={{ flex:1, background:'#fff', color:'#374151', border:'1.5px solid #d1d5db', borderRadius:10, padding:'11px 0', fontWeight:700, fontSize:13, cursor:'pointer' }}>{t('रद्द','Cancel')}</button>
              <button onClick={saveSeekerProfile} disabled={submitting} style={{ flex:2, background:submitting?'#9ca3af':M, color:'#fff', border:'none', borderRadius:10, padding:'11px 0', fontWeight:800, fontSize:13, cursor:submitting?'not-allowed':'pointer' }}>
                {submitting ? t('सहेज रहा है…','Saving…') : t('Profile Save करें ✅','Save Profile ✅')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── My Posted Jobs ── */}
      {view === 'mine' && (
        <div>
          {loading && <div style={{ textAlign:'center', padding:32 }}><Spin /></div>}
          {!loading && myJobs.length === 0 && (
            <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>
              <div>{t('आपने अभी कोई job post नहीं की','You have not posted any job yet.')}</div>
              <button onClick={() => setPostOpen(true)} style={{ marginTop:12, background:M, color:'#fff', border:'none', borderRadius:10, padding:'9px 18px', fontSize:13, fontWeight:800, cursor:'pointer' }}>+ {t('Post Job','Post Job')}</button>
            </div>
          )}
          {!loading && myJobs.map(j => {
            const sc = STATUS_CFG[j.status] || STATUS_CFG.Pending;
            return (
              <div key={j.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', padding:'12px 14px', marginBottom:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{j.title}</div>
                    {j.company_name && <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{j.company_name} · {j.location_city}</div>}
                    <div style={{ fontSize:10, color:'#9ca3af', marginTop:3 }}>{j.application_count || 0} {t('आवेदन','applicants')}</div>
                  </div>
                  <span style={{ background:sc.bg, color:sc.color, fontSize:10, fontWeight:700, borderRadius:20, padding:'2px 9px', flexShrink:0 }}>{sc.label}</span>
                </div>
                {j.admin_notes && <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:'6px 10px', marginTop:8, fontSize:11, color:'#991b1b' }}><strong>Note:</strong> {j.admin_notes}</div>}
                <div style={{ display:'flex', gap:8, marginTop:8 }}>
                  {j.status === 'Approved' && (
                    <button onClick={() => fetchApplicants(j)} style={{ flex:1, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'7px 0', fontSize:11, fontWeight:700, color:'#1d4ed8', cursor:'pointer' }}>
                      {t('आवेदक देखें','View Applicants')}
                    </button>
                  )}
                  {j.status !== 'Approved' && (
                    <button onClick={() => deleteJob(j.id)} style={{ flex:1, background:'#fff', border:'1px solid #fca5a5', borderRadius:8, padding:'7px 0', fontSize:11, fontWeight:700, color:'#dc2626', cursor:'pointer' }}>
                      {t('हटाएं','Delete')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── My Applications ── */}
      {view === 'applied' && (
        <div>
          {loading && <div style={{ textAlign:'center', padding:32 }}><Spin /></div>}
          {!loading && myApps.length === 0 && (
            <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>
              {t('आपने अभी किसी job के लिए apply नहीं किया।','You have not applied to any job yet.')}
            </div>
          )}
          {!loading && myApps.map(a => {
            const sc = { pending:{bg:'#fffbeb',color:'#92400e',l:'⏳ Pending'}, viewed:{bg:'#eff6ff',color:'#1d4ed8',l:'👁 Viewed'}, shortlisted:{bg:'#f0fdf4',color:'#15803d',l:'⭐ Shortlisted'}, rejected:{bg:'#fef2f2',color:'#991b1b',l:'❌ Rejected'} }[a.app_status] || {};
            return (
              <div key={a.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', padding:'12px 14px', marginBottom:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{a.title}</div>
                    {a.company_name && <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{a.company_name} · {a.location_city}</div>}
                    <div style={{ fontSize:10, color:'#9ca3af', marginTop:3 }}>{t('Applied','Applied')}: {new Date(a.applied_at).toLocaleDateString('en-IN')}</div>
                  </div>
                  <span style={{ background:sc.bg, color:sc.color, fontSize:10, fontWeight:700, borderRadius:20, padding:'2px 9px', flexShrink:0 }}>{sc.l}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Post Job Modal ── */}
      {postOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={e => e.target===e.currentTarget && setPostOpen(false)}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:520, maxHeight:'92vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ background:`linear-gradient(135deg,${M},#b71c1c)`, padding:'16px 20px', flexShrink:0, position:'relative' }}>
              <div style={{ fontSize:16, fontWeight:900, color:'#fff' }}>{t('नौकरी Post करें','Post a Job')}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.65)', marginTop:2 }}>{t('Admin review के बाद प्रकाशित होगी','Published after admin review')}</div>
              <button onClick={() => setPostOpen(false)} style={{ position:'absolute', top:12, right:14, width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            <div style={{ overflowY:'auto', padding:'16px 18px 8px', flex:1 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={lStyle}>{t('Job Title *','Job Title *')}</label><input style={iStyle} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} maxLength={255}/></div>
                <div><label style={lStyle}>{t('Company/Organization','Company/Org')}</label><input style={iStyle} value={form.company_name} onChange={e=>setForm(f=>({...f,company_name:e.target.value}))} maxLength={200}/></div>
                <div><label style={lStyle}>{t('Category','Category')}</label>
                  <select style={iStyle} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                    {JOB_CATS.map(c=><option key={c} value={c}>{JOB_CAT_LABEL[c]}</option>)}
                  </select>
                </div>
                <div><label style={lStyle}>{t('Job Type','Job Type')}</label>
                  <select style={iStyle} value={form.job_type} onChange={e=>setForm(f=>({...f,job_type:e.target.value}))}>
                    {JOB_TYPES.map(tp=><option key={tp} value={tp}>{tp}</option>)}
                  </select>
                </div>
                <div><label style={lStyle}>{t('City *','City *')}</label><input style={iStyle} value={form.location_city} onChange={e=>setForm(f=>({...f,location_city:e.target.value}))} maxLength={100}/></div>
                <div><label style={lStyle}>{t('State','State')}</label><input style={iStyle} value={form.location_state} onChange={e=>setForm(f=>({...f,location_state:e.target.value}))} maxLength={100}/></div>
                <div><label style={lStyle}>{t('Salary Range','Salary Range')}</label><input style={iStyle} value={form.salary_range} onChange={e=>setForm(f=>({...f,salary_range:e.target.value}))} placeholder="₹15,000-25,000/month" maxLength={100}/></div>
                <div><label style={lStyle}>{t('Contact Name','Contact Name')}</label><input style={iStyle} value={form.contact_name} onChange={e=>setForm(f=>({...f,contact_name:e.target.value}))} maxLength={200}/></div>
                <div><label style={lStyle}>{t('Contact Phone','Contact Phone')}</label><input style={iStyle} value={form.contact_phone} onChange={e=>setForm(f=>({...f,contact_phone:e.target.value}))} maxLength={15}/></div>
                <div><label style={lStyle}>{t('Last Date','Last Date')}</label><input type="date" style={iStyle} value={form.last_date} onChange={e=>setForm(f=>({...f,last_date:e.target.value}))}/></div>
                <div style={{ gridColumn:'1/-1' }}><label style={lStyle}>{t('Job Description','Job Description')}</label><textarea style={{ ...iStyle, height:80, resize:'vertical' }} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} maxLength={3000}/></div>
                <div style={{ gridColumn:'1/-1' }}><label style={lStyle}>{t('Requirements','Requirements')}</label><textarea style={{ ...iStyle, height:60, resize:'vertical' }} value={form.requirements} onChange={e=>setForm(f=>({...f,requirements:e.target.value}))} maxLength={2000}/></div>
                <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8 }}>
                  <input type="checkbox" id="remote" checked={!!form.is_remote} onChange={e=>setForm(f=>({...f,is_remote:e.target.checked}))}/>
                  <label htmlFor="remote" style={{ fontSize:13, cursor:'pointer' }}>{t('Remote Job है','This is a Remote Job')}</label>
                </div>
              </div>
            </div>
            <div style={{ padding:'12px 18px 16px', borderTop:'1px solid #e5e7eb', display:'flex', gap:10, flexShrink:0 }}>
              <button onClick={() => setPostOpen(false)} style={{ flex:1, background:'#fff', color:'#374151', border:'1.5px solid #d1d5db', borderRadius:10, padding:'11px 0', fontWeight:700, fontSize:13, cursor:'pointer' }}>{t('रद्द','Cancel')}</button>
              <button onClick={postJob} disabled={submitting} style={{ flex:2, background:submitting?'#9ca3af':M, color:'#fff', border:'none', borderRadius:10, padding:'11px 0', fontWeight:800, fontSize:13, cursor:submitting?'not-allowed':'pointer' }}>
                {submitting ? t('Submit हो रहा है…','Submitting…') : t('Submit for Review ✅','Submit for Review ✅')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Job Detail Modal ── */}
      {viewJob && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={e => e.target===e.currentTarget && setViewJob(null)}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:500, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ background:`linear-gradient(135deg,${JOB_TYPE_COLOR[viewJob.job_type]||M},#b71c1c)`, padding:'16px 20px', flexShrink:0, position:'relative' }}>
              <div style={{ fontSize:16, fontWeight:900, color:'#fff', paddingRight:32 }}>{viewJob.title}</div>
              {viewJob.company_name && <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:2 }}>{viewJob.company_name}</div>}
              <button onClick={() => setViewJob(null)} style={{ position:'absolute', top:12, right:14, width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.18)', border:'none', color:'#fff', fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            <div style={{ overflowY:'auto', padding:'16px 18px', flex:1 }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
                <span style={{ background:`${JOB_TYPE_COLOR[viewJob.job_type]||M}18`, color:JOB_TYPE_COLOR[viewJob.job_type]||M, borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>{viewJob.job_type}</span>
                {viewJob.category && viewJob.category !== 'private' && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'3px 10px', fontSize:11 }}>{JOB_CAT_LABEL[viewJob.category]||viewJob.category}</span>}
                {viewJob.location_city && <span style={{ background:'#f3f4f6', color:'#374151', borderRadius:20, padding:'3px 10px', fontSize:11 }}><EnvironmentOutlined style={{ fontSize:10 }}/> {viewJob.location_city}{viewJob.location_state?`, ${viewJob.location_state}`:''}{viewJob.location_country && viewJob.location_country!=='India'?` · ${viewJob.location_country}`:''}</span>}
                {viewJob.is_remote===1 && <span style={{ background:'#eff6ff', color:'#1d4ed8', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>Remote</span>}
                {viewJob.salary_range && <span style={{ background:'#f0fdf4', color:'#15803d', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:600 }}>₹ {viewJob.salary_range}</span>}
              </div>
              {viewJob.description  && <><div style={{ fontSize:12, fontWeight:700, color:M, marginBottom:5 }}>{t('Job Description','Job Description')}</div><div style={{ fontSize:13, color:'#374151', lineHeight:1.75, marginBottom:14, whiteSpace:'pre-wrap' }}>{viewJob.description}</div></>}
              {viewJob.requirements && <><div style={{ fontSize:12, fontWeight:700, color:M, marginBottom:5 }}>{t('Requirements','Requirements')}</div><div style={{ fontSize:13, color:'#374151', lineHeight:1.75, marginBottom:14, whiteSpace:'pre-wrap' }}>{viewJob.requirements}</div></>}
              {viewJob.poster_name  && <div style={{ fontSize:11, color:'#9ca3af', marginBottom:10 }}>{t('Posted by','Posted by')}: <strong style={{ color:M }}>{viewJob.poster_name}</strong>{viewJob.poster_village ? ` · ${viewJob.poster_village}` : ''}</div>}
              {viewJob.last_date    && <div style={{ background:'#fef2f2', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#dc2626', marginBottom:14, fontWeight:600 }}><ClockCircleOutlined/> {t('अंतिम तिथि','Last date')}: {new Date(viewJob.last_date).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})}</div>}
              {viewJob.contact_name && <div style={{ fontSize:12, color:'#374151', marginBottom:6 }}><UserOutlined/> {viewJob.contact_name}</div>}
              {viewJob.contact_phone && (
                <a href={`tel:${viewJob.contact_phone}`} style={{ display:'flex', alignItems:'center', gap:8, background:M, color:'#fff', borderRadius:10, padding:'12px 18px', textDecoration:'none', fontWeight:700, fontSize:14, justifyContent:'center' }}>
                  <PhoneOutlined /> {viewJob.contact_phone} — {t('Call to Apply','Call to Apply')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Apply Modal ── */}
      {applying && (
        <div style={{ position:'fixed', inset:0, zIndex:1001, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'flex-end', justifyContent:'center', padding:16 }}
          onClick={e => e.target===e.currentTarget && setApplying(null)}>
          <div style={{ background:'#fff', borderRadius:'16px 16px 0 0', width:'100%', maxWidth:480, padding:'20px', boxShadow:'0 -8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 16px' }}/>
            <div style={{ fontSize:14, fontWeight:800, color:M, marginBottom:4 }}>{applying.title}</div>
            <div style={{ fontSize:11, color:'#6b7280', marginBottom:12 }}>{applying.company_name} · {applying.location_city}</div>
            <label style={lStyle}>{t('आवेदन संदेश (वैकल्पिक)','Message (optional)')}</label>
            <textarea style={{ ...iStyle, height:80, resize:'vertical', marginBottom:12 }} value={applyMsg} onChange={e=>setApplyMsg(e.target.value)} placeholder={t('अपने बारे में संक्षेप में बताएं…','Brief introduction about yourself…')} maxLength={500}/>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setApplying(null)} style={{ flex:1, background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:10, padding:'11px 0', fontWeight:700, fontSize:13, cursor:'pointer', color:'#374151' }}>{t('रद्द','Cancel')}</button>
              <button onClick={submitApply} disabled={applyLoading} style={{ flex:2, background:applyLoading?'#9ca3af':M, color:'#fff', border:'none', borderRadius:10, padding:'11px 0', fontWeight:800, fontSize:13, cursor:applyLoading?'not-allowed':'pointer' }}>
                {applyLoading ? t('भेज रहे हैं…','Sending…') : t('आवेदन भेजें ✅','Send Application ✅')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Applicants Modal ── */}
      {applicants && (
        <div style={{ position:'fixed', inset:0, zIndex:1001, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={e => e.target===e.currentTarget && setApplicants(null)}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:480, maxHeight:'85vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ background:`linear-gradient(135deg,${M},#b71c1c)`, padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div style={{ fontSize:14, fontWeight:800, color:'#fff' }}>{t('आवेदक','Applicants')} — {applicants.job?.title}</div>
              <button onClick={() => setApplicants(null)} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'50%', width:28, height:28, color:'#fff', fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>
            <div style={{ overflowY:'auto', padding:'14px', flex:1 }}>
              {applicants.loading && <div style={{ textAlign:'center', padding:32 }}><Spin /></div>}
              {!applicants.loading && applicants.list.length === 0 && (
                <div style={{ textAlign:'center', padding:32, color:'#9ca3af' }}>{t('अभी कोई आवेदन नहीं है।','No applications yet.')}</div>
              )}
              {!applicants.loading && applicants.list.map(a => (
                <div key={a.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', padding:'11px 13px', marginBottom:10, display:'flex', gap:10, alignItems:'flex-start' }}>
                  <div style={{ flexShrink:0 }}>
                    {a.photo ? <img src={a.photo} alt={a.name} style={{ width:40, height:40, borderRadius:'50%', objectFit:'cover', objectPosition:'top', border:`2px solid ${M}` }}/> :
                      <div style={{ width:40, height:40, borderRadius:'50%', background:M, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#FFD700' }}>{(a.name||'?')[0]}</div>}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{a.name}</div>
                    {a.village && <div style={{ fontSize:10, color:'#9ca3af' }}>{a.village}</div>}
                    {a.message && <div style={{ fontSize:11, color:'#374151', marginTop:4, background:'#f9fafb', borderRadius:6, padding:'5px 8px', lineHeight:1.5 }}>{a.message}</div>}
                    <a href={`tel:${a.phone}`} style={{ display:'inline-flex', alignItems:'center', gap:5, marginTop:7, background:M, color:'#fff', borderRadius:8, padding:'5px 12px', textDecoration:'none', fontSize:12, fontWeight:700 }}>
                      <PhoneOutlined /> {a.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

